import React, { useState, useMemo } from 'react';
import { IoFilterSharp, IoClose } from 'react-icons/io5';
import * as Slider from '@radix-ui/react-slider';
import clsx from 'clsx';

import Product from './Product';
import ProductSkeleton from './ProductSkeleton';
import useFetch from '../hooks/useFetch';

const ProductGridWithFilter = ({ data = [] }) => {
  const rate = useFetch('/rates');

  const MIN_PRICE = 494500;
  const MAX_PRICE = 21949000;

  const [priceRange, setPriceRange] = useState([MIN_PRICE, MAX_PRICE]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedColor, setSelectedColor] = useState('');
  const [sortBy, setSortBy] = useState('default'); 
  const [filterOpen, setFilterOpen] = useState(false);

  const brands = ['Canon', 'SONY', 'Nikon', 'Fujifilm', 'Panasonic', 'DJI', 'GoPro', 'Insta360'];
  const colors = ['Черный', 'Белый', 'Серый', 'Серебряный', 'Красный', 'Зеленый', "Синий"];

  const handleBrandChange = (brand) => {
    setSelectedBrands((prev) =>
      prev.includes(brand) ? prev.filter((b) => b !== brand) : [...prev, brand]
    );
  };

  const filteredAndSortedData = useMemo(() => {
    if (!data || data.length === 0) return [];

    // Фильтрация
    let filtered = data.filter((product) => {
      const productPrice = product.price || 0;
      const priceInSum = rate.data?.[0]?.rate
        ? productPrice * rate.data[0].rate
        : productPrice;

      if (priceInSum < priceRange[0] || priceInSum > priceRange[1]) return false;
      if (selectedBrands.length > 0 && !selectedBrands.includes(product.brand)) return false;
      if (selectedColor && product.color?.color_name && product.color.color_name !== selectedColor) return false;

      return true;
    });

    // Сортировка
    const sorted = [...filtered];
    switch (sortBy) {
      case 'price-asc':
        sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
        break;
      case 'price-desc':
        sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
        break;
      case 'newest':
        sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'popular':
        // Можно добавить логику по популярности (например, по ID в обратном порядке)
        sorted.sort((a, b) => b.id - a.id);
        break;
      default:
        // По умолчанию - как пришло с сервера
        break;
    }

    return sorted;
  }, [data, priceRange, selectedBrands, selectedColor, sortBy, rate.data]);

  const isLoading = data === null || data === undefined;

  const activeFiltersCount =
    selectedBrands.length +
    (selectedColor ? 1 : 0) +
    (priceRange[0] !== MIN_PRICE || priceRange[1] !== MAX_PRICE ? 1 : 0);

  const resetFilters = () => {
    setPriceRange([MIN_PRICE, MAX_PRICE]);
    setSelectedBrands([]);
    setSelectedColor('');
    setSortBy('default');
  };

  // Табы сортировки
  const sortTabs = [
    { label: "По цене", value: 'price-asc' },
    { label: "По рейтингу", value: 'popular' },
    { label: "Новые товары", value: 'newest' },
    { label: "По популярности", value: 'price-desc' }
  ];

  const sortTabsDesktop = [
    { label: "По цене", value: 'price-asc' },
    { label: "По рейтингу", value: 'popular' },
    { label: "Новые товары", value: 'newest' },
    { label: "По популярности", value: 'price-desc' }
  ];

  return (
    <>
      <section className="py-8">
        <div className="flex flex-col lg:flex-row gap-8">

          {/* ДЕСКТОПНЫЙ ФИЛЬТР */}
          <aside className="hidden lg:block lg:w-80 flex-shrink-0">
            <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/10 shadow-2xl space-y-8">
              {/* Цена */}
              <div>
                <h3 className="text-white font-bold text-lg mb-4">Цена (сум)</h3>
                <div className="flex items-center gap-4 mb-6">
                  <input
                    type="text"
                    value={priceRange[0].toLocaleString()}
                    readOnly
                    className="w-full bg-white/20 rounded-lg px-4 py-3 text-center text-sm text-white"
                  />
                  <span className="text-white">—</span>
                  <input
                    type="text"
                    value={priceRange[1].toLocaleString()}
                    readOnly
                    className="w-full bg-white/20 rounded-lg px-4 py-3 text-center text-sm text-white"
                  />
                </div>

                <Slider.Root
                  className="relative flex items-center select-none touch-none w-full h-10"
                  value={priceRange}
                  onValueChange={setPriceRange}
                  max={MAX_PRICE}
                  min={MIN_PRICE}
                  step={100000}
                  minStepsBetweenThumbs={1}
                >
                  <Slider.Track className="bg-white/20 relative grow rounded-full h-2">
                    <Slider.Range className="absolute bg-accent rounded-full h-full" />
                  </Slider.Track>
                  <Slider.Thumb className="block w-6 h-6 bg-white rounded-full shadow-lg border-4 border-accent focus:outline-none focus:ring-4 focus:ring-amber-300/50" />
                  <Slider.Thumb className="block w-6 h-6 bg-white rounded-full shadow-lg border-4 border-accent focus:outline-none focus:ring-4 focus:ring-amber-300/50" />
                </Slider.Root>
              </div>

              {/* Бренд */}
              <div>
                <h3 className="text-white font-bold text-lg mb-4">Бренд</h3>
                <div className="space-y-3">
                  {brands.map((brand) => (
                    <label key={brand} className="flex items-center gap-3 cursor-pointer text-white/90 hover:text-white transition">
                      <input
                        type="checkbox"
                        checked={selectedBrands.includes(brand)}
                        onChange={() => handleBrandChange(brand)}
                        className="w-5 h-5 rounded border-white/50 text-accent focus:ring-accent"
                      />
                      <span>{brand}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Цвет */}
              <div>
                <h3 className="text-white font-bold text-lg mb-4">Цвет</h3>
                <div className="grid grid-cols-3 gap-3">
                  {colors.map((color) => (
                    <button
                      key={color}
                      onClick={() => setSelectedColor(prev => prev === color ? '' : color)}
                      className={clsx(
                        'py-3 px-2 rounded-xl text-xs font-medium transition whitespace-nowrap',
                        selectedColor === color
                          ? 'bg-accent text-white shadow-lg'
                          : 'bg-white/20 text-white/80 hover:bg-white/30'
                      )}
                    >
                      {color}
                    </button>
                  ))}
                </div>
              </div>

              <button
                onClick={resetFilters}
                className="w-full bg-white/20 hover:bg-white/30 text-white font-bold py-4 rounded-2xl transition"
              >
                Сбросить
              </button>
            </div>
          </aside>

          {/* ОСНОВНАЯ ЧАСТЬ */}
          <div className="flex-1">

            {/* МОБИЛЬНАЯ ВЕРХНЯЯ ПАНЕЛЬ: Табы + Кнопка фильтра */}
            <div className="lg:hidden mb-8">
              <div className="flex items-center justify-between gap-4 overflow-x-auto pb-3 scrollbar-hide">
                <div className="flex gap-5 flex-1">
                  {sortTabs.map((tab) => (
                    <button
                      key={tab.value}
                      onClick={() => setSortBy(tab.value)}
                      className={clsx(
                        "font-medium text-sm whitespace-nowrap transition",
                        sortBy === tab.value
                          ? "text-white border-b-2 border-accent pb-1"
                          : "text-white/70 hover:text-white"
                      )}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>

                <button
                  onClick={() => setFilterOpen(true)}
                  className="relative flex-shrink-0 p-3 bg-accent/20 hover:bg-accent/30 rounded-xl transition backdrop-blur-md border border-accent/30"
                >
                  <IoFilterSharp className="text-xl text-white" />
                  {activeFiltersCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-accent text-white text-xs font-bold rounded-full min-w-5 h-5 flex items-center justify-center shadow-lg animate-pulse">
                      {activeFiltersCount}
                    </span>
                  )}
                </button>
              </div>
            </div>

            {/* ДЕСКТОПНЫЕ ТАБЫ */}
            <div className="hidden lg:flex gap-6 border-b border-white/20 mb-8 pb-2">
              {sortTabsDesktop.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => setSortBy(tab.value)}
                  className={clsx(
                    "pb-3 font-medium transition whitespace-nowrap border-b-2",
                    sortBy === tab.value
                      ? "text-white border-accent"
                      : "text-white/70 hover:text-white border-transparent hover:border-accent"
                  )}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* ТОВАРЫ */}
            {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {[...Array(8)].map((_, i) => <ProductSkeleton key={i} />)}
              </div>
            ) : filteredAndSortedData.length === 0 ? (
              <div className="text-center py-20">
                <div className="text-white/60 text-xl">Товары не найдены</div>
                <p className="text-white/40 mt-2">Измените фильтры</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredAndSortedData.map((product) => (
                  <Product key={product.id} product={product} data={rate.data} />
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* МОБИЛЬНЫЙ ФИЛЬТР — BOTTOM SHEET */}
      {filterOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/70 z-40 lg:hidden"
            onClick={() => setFilterOpen(false)}
          />

          <div className="fixed inset-x-0 bottom-0 z-50 lg:hidden">
            <div className="bg-primary rounded-t-3xl shadow-2xl">
              <div className="py-4 text-center">
                <div className="w-12 h-1.5 bg-white/30 rounded-full mx-auto" />
              </div>

              <div className="px-6 pb-10 pt-2 max-h-[85vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-white">Фильтры</h2>
                  <button
                    onClick={() => setFilterOpen(false)}
                    className="text-white/70 hover:text-white text-3xl"
                  >
                    <IoClose />
                  </button>
                </div>

                {/* Цена */}
                <div className="mb-10">
                  <h3 className="text-white font-bold text-lg mb-6">Диапозон цен</h3>
                  <div className="flex justify-between text-white/80 text-sm mb-6">
                    <span>{priceRange[0].toLocaleString()} so'm</span>
                    <span>{priceRange[1].toLocaleString()} so'm</span>
                  </div>
                  <Slider.Root
                    className="relative flex items-center select-none touch-none w-full h-12"
                    value={priceRange}
                    onValueChange={setPriceRange}
                    max={MAX_PRICE}
                    min={MIN_PRICE}
                    step={100000}
                    minStepsBetweenThumbs={1}
                  >
                    <Slider.Track className="bg-white/20 relative grow rounded-full h-2">
                      <Slider.Range className="absolute bg-accent rounded-full h-full" />
                    </Slider.Track>
                    <Slider.Thumb className="block w-7 h-7 bg-white rounded-full shadow-xl border-4 border-accent focus:outline-none focus:ring-4 focus:ring-amber-300/50" />
                    <Slider.Thumb className="block w-7 h-7 bg-white rounded-full shadow-xl border-4 border-accent focus:outline-none focus:ring-4 focus:ring-amber-300/50" />
                  </Slider.Root>
                </div>

                {/* Бренды */}
                <div className="mb-10">
                  <h3 className="text-white font-bold text-lg mb-4">Бренд</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {brands.map((brand) => (
                      <label
                        key={brand}
                        className={clsx(
                          'flex items-center justify-center py-4 rounded-xl border-2 transition text-sm font-medium cursor-pointer',
                          selectedBrands.includes(brand)
                            ? 'bg-accent text-white border-accent shadow-md'
                            : 'bg-white/10 text-white/80 border-white/20 hover:bg-white/20'
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={selectedBrands.includes(brand)}
                          onChange={() => handleBrandChange(brand)}
                          className="sr-only"
                        />
                        {brand}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Цвет */}
                <div className="mb-10">
                  <h3 className="text-white font-bold text-lg mb-4">Цвет</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {colors.map((color) => (
                      <button
                        key={color}
                        onClick={() => setSelectedColor(prev => prev === color ? '' : color)}
                        className={clsx(
                          'py-4 px-2 rounded-xl text-xs font-medium transition whitespace-nowrap',
                          selectedColor === color
                            ? 'bg-accent text-white shadow-lg'
                            : 'bg-white/20 text-white/80 hover:bg-white/30'
                        )}
                      >
                        {color}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Кнопки */}
                <div className="flex gap-4">
                  <button
                    onClick={resetFilters}
                    className="flex-1 bg-white/20 hover:bg-white/30 text-white font-bold py-4 rounded-2xl transition"
                  >
                    Сбросить
                  </button>
                  <button
                    onClick={() => setFilterOpen(false)}
                    className="flex-1 bg-accent hover:bg-amber-500 text-white font-bold py-4 rounded-2xl transition shadow-lg"
                  >
                    Показать ({filteredAndSortedData.length})
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ProductGridWithFilter;